'use client';
import { useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

// Filter selections live in the URL (one comma-separated param per facet identifier). Toggling a
// value updates the URL, which re-runs the server page with the new productFilters.
export function usePlpFilters() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isChecked = useCallback(
    (identifier: string, value: string) => (searchParams.get(identifier)?.split(',') ?? []).includes(value),
    [searchParams],
  );

  const toggle = useCallback(
    (identifier: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      const current = params.get(identifier)?.split(',').filter(Boolean) ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      if (next.length) params.set(identifier, next.join(','));
      else params.delete(identifier);
      // Changing a filter can shrink the result below the current page — always return to page 1.
      params.delete('page');
      router.push(params.toString() ? `${pathname}?${params.toString()}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams],
  );

  // Sort rides the same URL. The default option clears the param so the canonical URL stays clean.
  const setSort = useCallback(
    (value: string, defaultValue: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value && value !== defaultValue) params.set('sort', value);
      else params.delete('sort');
      params.delete('page');
      router.push(params.toString() ? `${pathname}?${params.toString()}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams],
  );

  return { isChecked, toggle, setSort };
}
